/**
 * Shared action handlers for PACS and Worklist tables
 * Following DRY principle to avoid code duplication
 */

import { Modal, message } from 'antd';
import {
  makePostCall,
  makeGetCall,
  hasStudyViewingPermission,
  hasReportingPermission,
  RADIOLOGY_URL,
  getUserDetails,
} from './helper';
import { launchViewerOnDualMonitor, openReportingWindow } from './MonitorManager';

/**
 * Per-user concurrent-report cap (users.user_report_limit; blank/0 = unlimited).
 *
 * Front-of-house only: it stops an oversized batch before a reporting tab is even
 * opened, so the user gets one clear message instead of discovering the cap study
 * by study. Real enforcement is server-side — getOrdersListDetails trims the queue
 * to the slots left, and the claim in getStudyDetails is the race-safe backstop.
 *
 * Counts studies about to be opened, not studies already held elsewhere, so a user
 * holding studies in another tab can still be trimmed by the backend.
 */
export const withinReportLimit = (count: number, userDetails: any): boolean => {
  const limit = parseInt(userDetails?.user_report_limit, 10);
  if (!Number.isFinite(limit) || limit <= 0) return true; // unlimited
  if (count <= limit) return true;

  const noun = limit === 1 ? 'study' : 'studies';
  message.info(
    `You can open at most ${limit} ${noun} at a time. Please select ${limit} or fewer.`
  );
  return false;
};

/**
 * Open DICOM viewer for a study
 */
export const openViewer = async (record: any, userDetails: any): Promise<void> => {
  if (hasStudyViewingPermission(userDetails)) {
    const viewerWindow = await launchViewerOnDualMonitor(record?.ps_study_uid, record);
    if (!viewerWindow) message.warning('Could not open viewer window');
  } else {
    message.info("You do not have permission to view study images");
  }
};

/**
 * Open external radiology desk
 */
export const openRadDesk = (record: any, userDetails: any): void => {
  if (hasStudyViewingPermission(userDetails)) {
    const { pacs_order } = record;
    const { patient, po_site } = pacs_order;
    const { pat_pin } = patient;
    window.open(`${RADIOLOGY_URL(pat_pin, po_site || '')}`, '_blank');
  } else {
    message.info("You do not have the permission to view the study images");
  }
};

/**
 * Is reporting on a HIS-unconfirmed study forbidden outright at this deployment?
 *
 * Centres differ on this. Where the HIS feed is reliable, an unconfirmed study means the
 * wrong scan is linked and reporting it is a real hazard, so it must be blocked. Where the
 * feed lags (or never arrives at all — order-less and item-less scans can never reconcile),
 * blocking strands emergencies behind something the radiologist cannot influence, so a
 * warning they can accept is the right call. Hence a per-deployment flag, default off
 * (warn-and-allow).
 *
 * Read straight off window.ENV_CONFIG rather than through APP_CONSTANTS: that file is
 * gitignored and maintained per deployment, so a key added to it there would never reach
 * other installs. env-config.js is the runtime-editable file (no rebuild needed).
 */
export const isUnconfirmedReportingBlocked = (): boolean =>
  (typeof window !== 'undefined' && (window as any).ENV_CONFIG?.BLOCK_UNCONFIRMED_REPORTING) === true;

const isUnconfirmed = (rec: any): boolean => rec?.pacs_order?.po_his_status === 'UNCONFIRMED';
const isNotForReporting = (rec: any): boolean =>
  rec?.order_workflow?.ow_pacs_status === 'NOT_FOR_REPORTING';
/**
 * A study is CANCELLED if either side says so — the PACS workflow status or the
 * HIS status. Exported (as isCancelledStudy) so every caller that must not open a
 * cancelled study for reporting tests the same two fields.
 */
export const isCancelledStudy = (rec: any): boolean =>
  rec?.order_workflow?.ow_pacs_status === 'CANCELLED' || rec?.pacs_order?.po_his_status === 'CANCELLED';
const isCancelled = isCancelledStudy;

/**
 * Ask the user to acknowledge reporting on studies whose HIS status is still UNCONFIRMED.
 * Only reached when BLOCK_UNCONFIRMED_REPORTING is off. Resolves true to go ahead.
 *
 * Kept string-only (no JSX) so this stays a .ts module usable from both the table handlers
 * and the page-level batch openers.
 */
const confirmUnconfirmedReporting = (
  unconfirmedCount: number = 1,
  totalCount?: number
): Promise<boolean> =>
  new Promise(resolve => {
    const isBatch = typeof totalCount === 'number' && totalCount > 1;
    // totalCount is the count actually being opened (already stripped of "Not for reporting"),
    // not the raw selection — so word it as "being opened" rather than "selected".
    const subject =
      unconfirmedCount >= (totalCount ?? 0)
        ? `All ${totalCount} studies being opened have`
        : `${unconfirmedCount} of the ${totalCount} studies being opened have`;
    Modal.confirm({
      title: isBatch ? 'Some studies are not confirmed in HIS' : 'Study not confirmed in HIS',
      content: isBatch
        ? `${subject} not been confirmed in HIS yet. Their order details may still change once `
          + 'they reconcile. Do you want to report them anyway?'
        : 'This study has not been confirmed in HIS yet. Its order details may still change once it '
          + 'reconciles. Do you want to report it anyway?',
      okText: 'Report anyway',
      cancelText: 'Cancel',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });

/**
 * Gate a SINGLE study's report open on the unconfirmed rule. Returns true if the caller
 * may proceed: always for a confirmed study, never when the flag blocks it, and only on
 * the user's acknowledgement otherwise.
 */
export const allowUnconfirmedReporting = async (record: any): Promise<boolean> => {
  if (!isUnconfirmed(record)) return true;
  if (isUnconfirmedReportingBlocked()) {
    message.info('Cannot report unconfirmed study');
    return false;
  }
  return confirmUnconfirmedReporting();
};

/**
 * Narrow a batch selection down to the studies that may be opened for reporting, emitting
 * the "skipped" messaging and — when unconfirmed studies are being kept — a single
 * acknowledgement dialog for the whole batch rather than one prompt per study.
 *
 * "Not for reporting" is always a hard block: it is a deliberate operational decision,
 * unlike an unconfirmed HIS status. Returns null when the caller should abort entirely.
 *
 * selectedCount is the raw selection size, which can exceed records.length on pages that
 * track keys and rows separately — it only affects the skipped count.
 */
export const filterReportableStudies = async (
  records: any[],
  selectedCount?: number
): Promise<any[] | null> => {
  const blockUnconfirmed = isUnconfirmedReportingBlocked();
  const reportable = records.filter(
    rec => !isNotForReporting(rec) && !isCancelled(rec) && !(blockUnconfirmed && isUnconfirmed(rec))
  );
  const blockedCount = (selectedCount ?? records.length) - reportable.length;

  if (!reportable.length) {
    message.info(
      blockUnconfirmed
        ? 'Selected studies are marked Not for reporting, Cancelled, or are unconfirmed'
        : 'Selected studies are marked Not for reporting or Cancelled'
    );
    return null;
  }
  if (blockedCount > 0) {
    const reason = blockUnconfirmed
      ? 'marked Not for reporting, Cancelled, or unconfirmed'
      : 'marked Not for reporting or Cancelled';
    message.info(`${blockedCount} study(ies) ${reason} were skipped`);
  }

  if (!blockUnconfirmed) {
    const unconfirmedCount = reportable.filter(isUnconfirmed).length;
    if (unconfirmedCount > 0) {
      const proceed = await confirmUnconfirmedReporting(unconfirmedCount, reportable.length);
      if (!proceed) return null;
    }
  }
  return reportable;
};

/**
 * Open report editor in new tab
 */
export const openReport = async (record: any, userDetails: any, disableViewer: boolean = false): Promise<void> => {
  if (hasReportingPermission(userDetails)) {
    if (record?.order_workflow?.ow_pacs_status === 'NOT_FOR_REPORTING') {
      message.info('This study is marked Not for reporting');
      return;
    }
    if (record?.order_workflow?.ow_pacs_status === 'CANCELLED' || record?.pacs_order?.po_his_status === 'CANCELLED') {
      message.info('This study is Cancelled and cannot be reported');
      return;
    }
    // Unconfirmed is blocked or merely warned about, depending on BLOCK_UNCONFIRMED_REPORTING.
    if (!(await allowUnconfirmedReporting(record))) return;
    const data: any = {
      order_ids: [record?.pacs_order?.pacs_ord_id].join(','),
    };
    if (disableViewer) {
      data.openviewer = 'disable';
    }
    const params = new URLSearchParams(data).toString();
    // A tab here, or a window on the other monitor, per the user's workstation layout.
    await openReportingWindow(`/oo-report?${params}`);
  } else {
    message.info("You do not have the permission to do reporting");
  }
};

/**
 * Open multiple selected studies for reporting
 */
export const openSelectedStudies = (selectedRowKeys: any[], userDetails: any): void => {
  if (hasReportingPermission(userDetails)) {
    const data = {
      order_ids: selectedRowKeys.join(','),
    };
    const params = new URLSearchParams(data).toString();
    window.open(`/report?${params}`, '_blank');
  } else {
    message.info("You do not have the permission to do reporting");
  }
};

/**
 * Print report PDF
 */
export const printReport = async (
  rec: any,
  setPrintLoading: (loading: boolean) => void
): Promise<void> => {
  setPrintLoading(true);
  const { pacs_order } = rec;
  try {
    const res = await makePostCall('/print-acc-report', {
      order_id: pacs_order.pacs_ord_id,
      user_id: getUserDetails()?.username,
    }, {
      responseType: "arraybuffer",
    });

    const pdfBlob = new Blob([res.data], { type: "application/pdf" });
    const pdf_url = URL.createObjectURL(pdfBlob);
    const printWindow = window.open(pdf_url, "_blank");
    printWindow.print();
  } catch (err) {
    console.log("Error", err);
    message.error("Failed to print report");
  } finally {
    setPrintLoading(false);
  }
};

/**
 * View report PDF in modal
 */
export const viewReport = async (
  rec: any,
  setPrintLoading: (loading: boolean) => void,
  setViewerModal: (modal: any) => void
): Promise<void> => {
  setPrintLoading(true);
  const { pacs_order } = rec;
  const { pacs_ord_id, patient } = pacs_order;
  const { pat_pacs_id } = patient;

  try {
    const res = await makePostCall('/print-acc-report', {
      order_id: pacs_order.pacs_ord_id,
      user_id: getUserDetails()?.username,
    }, {
      responseType: "arraybuffer",
    });

    setViewerModal({
      visible: true,
      data: res.data,
      patient_id: pat_pacs_id,
      order_id: pacs_ord_id,
      patientData: rec
    });
  } catch (err) {
    console.log("Error", err);
    message.error("Failed to view report");
    setViewerModal({ visible: false, data: null });
  } finally {
    setPrintLoading(false);
  }
};

/**
 * Open a blank popup window synchronously. Must be called directly inside the
 * click handler (before any `await`) so the browser still treats it as a
 * user-gesture and doesn't popup-block it. The caller points it at the PDF once
 * the blob is ready via `showPdfInWindow`.
 */
export const openReportWindow = (): Window | null => {
  const win = window.open('', '_blank', 'width=900,height=1000,menubar=no,toolbar=no');
  if (win) {
    win.document.write(
      '<title>Report</title><body style="margin:0;font-family:sans-serif;color:#555">' +
      '<div style="padding:16px">Loading report…</div></body>'
    );
    win.document.close();
  }
  return win;
};

/**
 * Point a window opened by `openReportWindow` at a PDF so Chrome renders it with
 * its native PDF viewer. Falls back to a fresh tab if the popup was blocked.
 */
export const showPdfInWindow = (
  win: Window | null,
  pdfData: Blob | ArrayBuffer
): void => {
  const blob =
    pdfData instanceof Blob ? pdfData : new Blob([pdfData], { type: 'application/pdf' });
  const blobUrl = URL.createObjectURL(blob);
  if (win && !win.closed) {
    win.location.href = blobUrl;
  } else {
    // Popup was blocked — best-effort fallback to a new tab.
    window.open(blobUrl, '_blank');
  }
  // Revoke once the browser has had time to load the document.
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
};

/**
 * View report PDF in Chrome's native PDF viewer (separate popup window) instead
 * of the in-app modal. Uses the same /print-acc-report source as `viewReport`.
 */
export const openReportInWindow = async (
  rec: any,
  setPrintLoading: (loading: boolean) => void
): Promise<void> => {
  const reportWindow = openReportWindow();
  setPrintLoading(true);
  try {
    const res = await makePostCall('/print-acc-report', {
      order_id: rec.pacs_order.pacs_ord_id,
      user_id: getUserDetails()?.username,
    }, {
      responseType: "arraybuffer",
    });

    showPdfInWindow(reportWindow, res.data);
  } catch (err) {
    console.log("Error", err);
    message.error("Failed to view report");
    if (reportWindow && !reportWindow.closed) reportWindow.close();
  } finally {
    setPrintLoading(false);
  }
};

/**
 * Download report as DOCX
 */
export const downloadDocxReport = async (
  rec: any,
  setPrintLoading: (loading: boolean) => void
): Promise<void> => {
  setPrintLoading(true);
  const { pacs_order } = rec;

  try {
    const res = await makeGetCall(`/download-pat-report-docx?order_id=${pacs_order.pacs_ord_id}`, {
      responseType: "arraybuffer",
    });

    // Extract filename from Content-Disposition header if available
    let filename = `report-${pacs_order.pacs_ord_id}-${pacs_order.patient.pat_name}.docx`;

    // Create blob and download
    const docxBlob = new Blob([res.data], {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    });
    const url = URL.createObjectURL(docxBlob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // Clean up
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);

    message.success('DOCX report downloaded successfully');
  } catch (err) {
    console.log("Error downloading DOCX report", err);
    message.error('Failed to download DOCX report');
  } finally {
    setPrintLoading(false);
  }
};

/**
 * Toggle feature states (emergency, confirmation, reporting block)
 */
export const toggleFeatures = async (
  rec: any,
  idx: number,
  feature: 'emergency' | 'confirmation' | 'reporting',
  current: number,
  pageSize: number,
  orders: any,
  setOrders: (orders: any) => void,
  filters: any,
  filterResults: (filters: any) => void
): Promise<void> => {
  const { pacs_order, order_workflow } = rec;
  const { pacs_ord_id, po_priority, po_his_status } = pacs_order;
  const { ow_reporting_blocked } = order_workflow;

  const shouldConfirm = po_his_status !== 'CONFIRMED';
  const currentlyBlocked = ow_reporting_blocked === true || ow_reporting_blocked === 'Y';
  const shouldBlock = !currentlyBlocked;
  const markEmergency = po_priority !== 'EMERGENCY';

  const payload: any = {
    user_id: getUserDetails()?.username,
    order_id: pacs_ord_id,
  };

  let url = '';

  switch (feature) {
    case 'emergency':
      payload.markEmergency = markEmergency;
      url = '/toggle-emergency';
      break;
    case 'confirmation':
      payload.shouldConfirm = shouldConfirm;
      url = '/toggle-confirmation';
      break;
    case 'reporting':
      payload.shouldBlock = shouldBlock;
      url = '/toggle-reporting';
      break;
    default:
      return;
  }

  try {
    await makePostCall(url, payload);
    message.success("Updated successfully");
    filterResults(filters);
  } catch (e) {
    console.log("Error while toggling feature", e);
    message.error(e.message);
  }
};

/**
 * Assign study to technician (self-assignment)
 */
export const assignToSelfTechnician = async (
  rec: any,
  idx: number,
  current: number,
  pageSize: number,
  orders: any,
  setOrders: (orders: any) => void
): Promise<void> => {
  const { pacs_order } = rec;
  const payload = {
    user_id: getUserDetails()?.username,
    order_id: pacs_order?.pacs_ord_id
  };
  const currentIndx = (current - 1) * pageSize + idx;

  try {
    await makePostCall('/assign-to-tech', payload);
    message.success("Updated successfully");
    const prevOrders = [...(orders?.data || [])];
    prevOrders[currentIndx].order_workflow.ow_assigned_technician = getUserDetails()?.username;
    setOrders({ data: prevOrders, loading: false });
  } catch (e) {
    console.log("Error while assigning", e);
    message.error(e.message);
  }
};

/**
 * Upload RIS notes or files
 */
export const uploadRisNotes = async (
  addFileModal: any,
  setAddFileModal: (modal: any) => void
): Promise<void> => {
  const { data, modalType, fileType, notes, file, isNotes } = addFileModal;
  const { pacs_order } = data;
  const formData = new FormData();

  formData.append('order_ids', pacs_order?.pacs_ord_id);
  formData.append('order_id', pacs_order?.pacs_ord_id);
  formData.append('pin', pacs_order?.patient?.pat_pin);

  let headers = {
    'Content-Type': 'multipart/form-data'
  };

  if (isNotes) {
    formData.append('type', 'notes');
    formData.append('notes', notes);
  } else {
    formData.append('file_type', fileType);
    formData.append('file', file);
  }

  const url = isNotes ? '/upload-notes' : '/upload-file';
  formData.append('user_id', getUserDetails()?.username);

  try {
    await makePostCall(url, formData, { headers });
    setAddFileModal(null);
    message.success("Uploaded successfully");
  } catch (error) {
    message.error('Failed to upload file');
  }
};

/**
 * Assign studies to user (HOD functionality)
 */
export const assignToUser = async (
  selectedRowKeys: any[],
  selectedUsersToAssign: any,
  setAssignModal: (modal: any) => void,
  filters: any,
  filterResults: (filters: any) => void
): Promise<void> => {
  setAssignModal({ visible: false, data: {} });

  try {
    await makePostCall('/assign-to-user', {
      order_ids: selectedRowKeys,
      assigned_to: selectedUsersToAssign
    });
    message.success("Assigned successfully");
    filterResults(filters);
  } catch (e) {
    console.log(e);
    message.error("Failed to assign");
  }
};

/**
 * Close report (remove lock)
 */
export const closeReport = async (data: any): Promise<void> => {
  try {
    await makePostCall("/close-report", { order_id: data?.pacs_order?.pacs_ord_id });
  } catch (e) {
    console.log("Error closing report", e);
  }
};
